/**
 * ArticleIndex (path-mode plan, Phase 3): a list of the project's live articles from articleRuntime.index[section.id]
 * (computed by the builder at publish and by `articles related refresh`). Contract: the root is
 * section[data-seg-index="<section.id>"] and never contains another section[data-seg-index]; the builder wraps it in
 * <!--seg:index:<id>:start-->…<!--seg:index:<id>:end-->. Empty list → a hidden root (its section wrapper collapses
 * through the style block, like ArticleRelated); no runtime at all (editor / draft preview) → a visible placeholder.
 * Visual: a ruled list, the whole row clickable through the title link (D-9; the link text is the page title, AC-023).
 * Which item fields show is the section's own toggles (AC-027). Dates use the article defaultLanguage so the SSR text
 * and the client re-render agree (R-14). Funnels never reach it (article context only).
 */
import React, { memo, useContext } from 'react';
import type { LayoutSection } from '@segmently/schema-models/v2';
import type { ArticleBlockProps } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/types';
import type { ArticleIndexRuntimeItem } from '@modules/screen-view-v2/types/articleRuntime';
import { articleLinkTargetProps } from '@modules/screen-view-v2/utils/articleLinks';
import { ArticleIndexPreviewContext } from '@modules/screen-view-v2/context/ArticleIndexPreviewContext';
import { formatArticleDate } from '@modules/screen-view-v2/utils/articleDate';
import { slugifyHeading } from '@modules/screen-view-v2/utils/articleSlug';
import { resolveLocaleText } from '@modules/screen-view-v2/utils/articleText';
import { ARTICLE_INDEX_PREVIEW_ATTRIBUTE } from '@modules/screen-view-v2/utils/articleIndexPreviewCss';

const DEFAULT_LIMIT = 12;
const MAX_LIMIT = 50;
const PLACEHOLDER_ROWS = 3;

const SORT_LABELS: Record<string, string> = { dateModified: 'Last updated', datePublished: 'First published', title: 'Title A–Z' };

/** C-24: what the placeholder says the list will contain; names come from the editor-only preview context. */
function previewNote(content: LayoutSection['articleIndexContent'], limit: number, names: { folders: Readonly<Record<string, string>>; topics: Readonly<Record<string, string>> } | null): string {
  const folderId = content?.filter?.folderId;
  const topicId = content?.filter?.topicId;
  const quoted = (kind: string, name: string | undefined) => (name ? `${kind} “${name}”` : kind);
  const filter = folderId ? quoted('Folder', names?.folders[folderId]) : topicId ? quoted('Topic', names?.topics[topicId]) : 'All articles';
  const sort = SORT_LABELS[content?.sort ?? 'dateModified'] ?? SORT_LABELS.dateModified;
  return `Preview — the list fills with your live articles when you publish. ${filter} · ${sort} · up to ${limit}.`;
}

interface IndexToggles {
  showDescription: boolean;
  showThumbnail: boolean;
  showTopic: boolean;
  showDate: boolean;
}

function IndexMeta({ item, toggles, language }: { item: ArticleIndexRuntimeItem; toggles: IndexToggles; language: string }): React.ReactElement | null {
  const topic = toggles.showTopic && item.topic ? item.topic : null;
  const date = toggles.showDate && item.dateModified ? formatArticleDate(item.dateModified, language) : null;
  if (!topic && !date) return null;
  return (
    <p className="ap-index-meta">
      {topic ? <span>{topic}</span> : null}
      {topic && date ? ' · ' : null}
      {date && item.dateModified ? <time dateTime={item.dateModified}>{date}</time> : null}
    </p>
  );
}

export const ArticleIndex = memo(function ArticleIndex({ section, locale, article }: ArticleBlockProps): React.ReactElement {
  const content = section.articleIndexContent;
  const limit = content?.limit && content.limit > 0 ? Math.min(content.limit, MAX_LIMIT) : DEFAULT_LIMIT;
  const toggles: IndexToggles = {
    showDescription: Boolean(content?.showDescription),
    showThumbnail: Boolean(content?.showThumbnail),
    showTopic: Boolean(content?.showTopic),
    showDate: Boolean(content?.showDate),
  };
  const preview = useContext(ArticleIndexPreviewContext);
  // C-22: an empty (or whitespace-only) heading means no heading.
  const heading = resolveLocaleText(content?.heading, locale).trim();
  const headingId = `ap-${slugifyHeading(section.id)}-heading`;

  if (!article.runtime) {
    return (
      // Under the Studio editor's preview context only: the hook for ARTICLE_INDEX_PREVIEW_CSS (the editor has no
      // article style block). Draft previews and published pages never carry it.
      <section
        className="ap-block ap-index ap-index--placeholder"
        data-seg-index={section.id}
        data-seg-index-placeholder=""
        {...(preview ? { [ARTICLE_INDEX_PREVIEW_ATTRIBUTE]: '' } : {})}
      >
        {heading ? <h2 className="ap-panel-title">{heading}</h2> : null}
        <ul className="ap-index-list" aria-hidden="true">
          {Array.from({ length: PLACEHOLDER_ROWS }, (_, row) => (
            <li className="ap-index-item" key={row}>
              {toggles.showThumbnail ? <span className="ap-index-cover" /> : null}
              <div className="ap-index-body">
                {toggles.showTopic || toggles.showDate ? <p className="ap-index-meta">{[toggles.showTopic ? 'Topic' : null, toggles.showDate ? 'Date' : null].filter(Boolean).join(' · ')}</p> : null}
                <h3 className="ap-index-title">Article title</h3>
                {toggles.showDescription ? <><span className="ap-index-ph-bar" style={{ width: '92%' }} /><span className="ap-index-ph-bar" style={{ width: '64%' }} /></> : null}
              </div>
            </li>
          ))}
        </ul>
        <p className="ap-index-ph-note">{previewNote(content, limit, preview ? { folders: preview.folderNames, topics: preview.topicLabels } : null)}</p>
      </section>
    );
  }

  const items = (article.runtime.index?.[section.id]?.items ?? []).slice(0, limit);
  if (items.length === 0) return <section className="ap-block ap-index" data-seg-index={section.id} hidden />;
  const siteOrigin = article.runtime.siteOrigin;
  return (
    <section className="ap-block ap-index" data-seg-index={section.id} aria-labelledby={heading ? headingId : undefined}>
      {heading ? <h2 id={headingId} className="ap-panel-title">{heading}</h2> : null}
      <ul className="ap-index-list">
        {items.map((item) => (
          <li className="ap-index-item" key={item.alias}>
            {toggles.showThumbnail && item.imageUrl ? <img className="ap-index-cover" src={item.imageUrl} alt="" loading="lazy" /> : null}
            <div className="ap-index-body">
              <IndexMeta item={item} toggles={toggles} language={article.defaultLanguage} />
              <h3 className="ap-index-title">
                <a className="ap-index-link" href={item.url} {...articleLinkTargetProps(item.url, undefined, siteOrigin)}>{item.title}</a>
              </h3>
              {toggles.showDescription && item.description ? <p className="ap-index-desc">{item.description}</p> : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
});

ArticleIndex.displayName = 'ArticleIndex';
